'use client'

import { useReducer, useRef } from 'react'

import { composerReducer, initialComposerState } from './review-composer-state'
import { ReviewOwnershipError, sendReview } from './review-flow/send-review'
import { initialReviewState } from './review-flow/state'
import type { ReviewComposerOptions, ReviewDraft } from './review-flow/types'

interface ComposerProps {
  releaseId: number
  score: number
  existingReviewId: number | null
  onScore: (score: number) => void
  onSubmitted: (reviewId: number, score: number) => void
  openModal: () => void
  closeModal: () => void
}

export const useReviewComposer = (props: ComposerProps) => {
  const [state, dispatch] = useReducer(composerReducer, initialComposerState)
  const pending = useRef(false)

  const loadOptions = async () => {
    if (state.options) {
      return state.options
    }
    const response = await fetch(`/api/release/${props.releaseId}/review-options`)
    if (!response.ok) {
      throw new Error('Review options could not be loaded')
    }
    const options = (await response.json()) as ReviewComposerOptions
    dispatch({ type: 'options', options })
    return options
  }

  const submit = async (intent: 'submit' | 'compose' = 'submit') => {
    if (pending.current || state.status === 'success' || props.score < 1 || props.score > 5) {
      return
    }
    pending.current = true
    dispatch({ type: 'status', status: 'loading' })
    try {
      const options = await loadOptions()
      const review = initialReviewState(options, props.score, state.message)
      if (intent === 'compose' || review.step === 'details') {
        dispatch({ type: 'status', status: 'idle' })
        props.openModal()
        return
      }
      dispatch({ type: 'status', status: 'submitting' })
      const reviewId = await sendReview({
        ...review,
        releaseId: props.releaseId,
        existingReviewId: props.existingReviewId,
      })
      dispatch({ type: 'submitted', reviewId })
      props.onSubmitted(reviewId, review.score)
    } catch (error) {
      dispatch({ type: 'status', status: error instanceof ReviewOwnershipError ? 'ownershipError' : 'error' })
    } finally {
      pending.current = false
    }
  }

  const closeModal = (draft: ReviewDraft) => {
    props.onScore(draft.score)
    dispatch({ type: 'message', message: draft.message })
    props.closeModal()
  }

  return { state, dispatch, submit, closeModal, busy: state.status === 'loading' || state.status === 'submitting' }
}
